import { useSuspenseQuery, useMutation, useQueryClient, queryOptions, QueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { axiosInstance } from '@/api/axios'
import {
  UnitsSettingsResponse,
  UnitSettingPostRequest,
  DirectCostUnitCost,
  DirectCostUnitCostPostBody,
  DirectCostUnitCostConfig,
  ColumnSwitchRequest,
  CogsTotalResultRequest,
  GetDirectLabourDataResponse,
  PostDirectLabourBody,
  DirectLabourColumnSwitchRequest,
  CogsTotalTable,
  DirectLabourModelingApproachSetting,
  GetDirectCostsCategoriesResponse
} from './types'
import { FinancialModelSettingsStatus, FinancialModelSettingsV2Model, FinancialModelSettingsV2Section } from '@/feature/financial-model/api/types'
import { getSingleFinancialModelSettingsV2 } from '@/feature/financial-model/api'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { useCogsStore } from '../store'
import { COGS_BASE_KEY, createQueryKey, TOTALS_KEY } from '@/api/query-keys'
import { handlePromises } from '@/lib/utils/promises'

// ***** GET - DIRECT COSTS - UNITS SETTINGS ******** //

const getUnitSettingsQueryKey = (financialModelId: string, categoryId?: string) =>
  createQueryKey({
    baseKey: COGS_BASE_KEY,
    keys: [`unit-settings`, financialModelId, categoryId]
  })

export const getUnitsSettings = async (financialModelId: string, categoryId?: string): Promise<UnitsSettingsResponse> => {
  try {
    const response = await axiosInstance.get(`/financial-models/${financialModelId}/cogs/unit-settings`, {
      params: categoryId ? { category_id: categoryId } : {}
    })
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      return {
        level_of_granularity: null,
        modeling_approach: null,
        scenario: 1,
        unit_settings: [],
        status: {},
        id: null,
        financial_model_id: financialModelId
      }
    }
    throw error
  }
}

// ***** POST - DIRECT COSTS - UNITS SETTINGS ******** //

export const unitSettingsQueryOptions = (financialModelId: string, categoryId?: string) =>
  queryOptions({
    queryKey: getUnitSettingsQueryKey(financialModelId, categoryId),
    queryFn: () => getUnitsSettings(financialModelId, categoryId),
    select: data => ({
      ...data,
      unit_settings: data.unit_settings.sort((a, b) => a.product_id?.localeCompare(b.product_id ?? ``) ?? 0)
    })
  })

export const useSuspenseUnitsSettings = (financialModelId: string, categoryId?: string) =>
  useSuspenseQuery(unitSettingsQueryOptions(financialModelId, categoryId))

interface PostUnitsSettingsParams {
  financialModelId: string
  data: UnitSettingPostRequest
}

export const postUnitsSettings = async ({ financialModelId, data }: PostUnitsSettingsParams): Promise<UnitsSettingsResponse> => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/cogs/unit-settings`, data)
  return response.data
}

export const usePostUnitsSettings = (financialModelId: string, categoryId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UnitSettingPostRequest) => postUnitsSettings({ financialModelId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getUnitSettingsQueryKey(financialModelId, categoryId)
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: COGS_BASE_KEY,
          keys: [`direct-costs`, financialModelId]
        })
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `direct-costs`]
        })
      })
    }
  })
}

// ***** COGS FEATURE SETTINGS ******** //
export const useGetCogsSuspenseSettings = (params: {
  id: string
  module?: FinancialModelSettingsV2Model
  section?: FinancialModelSettingsV2Section
}) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`settings`, params.module, params.section].filter((value): value is string => value !== undefined)
    }),
    queryFn: () => getSingleFinancialModelSettingsV2(params),
    select: data => ({
      completion_status: data.cogs.completion_status,
      status: data.cogs.settings.reduce(
        (acc, curr) => {
          if (curr.type) {
            acc[curr.type] = curr.status
          }
          return acc
        },
        {
          direct_costs: `not_started`,
          direct_labour: `not_started`
        } as Record<string, FinancialModelSettingsStatus>
      ),
      settings: data.cogs.settings
    }),
    staleTime: 0
  })

// **** GET -> DIRECT COSTS - CATEGORIES **** //

const getDirectCostsCategoriesQueryKey = (financialModelId: string) =>
  createQueryKey({
    baseKey: COGS_BASE_KEY,
    keys: [`direct-costs`, `categories`, financialModelId]
  })

const getCogsDirectCostsCategories = async (financialModelId: string) => {
  const response = await axiosInstance.get<GetDirectCostsCategoriesResponse>(`/financial-models/${financialModelId}/cogs/categories`)
  return response.data
}

export const useGetCogsDirectCostsCategories = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: getDirectCostsCategoriesQueryKey(financialModelId),
    queryFn: () => getCogsDirectCostsCategories(financialModelId)
  })

// **** POST -> DIRECT COSTS - CATEGORY **** //

const postCategory = async ({ financialModelId, name = `` }: { financialModelId: string; name: string }) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/cogs/categories`, {
    name
  })
  return response.data
}

export const usePostCategoryMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof postCategory>[0]) => postCategory(data),
    mutationKey: getDirectCostsCategoriesQueryKey(financialModelId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getDirectCostsCategoriesQueryKey(financialModelId)
      })
    }
  })
}

// **** DELETE -> DIRECT COSTS - CATEGORY **** //
const deleteCategory = async ({ financialModelId, categoryId }: { financialModelId: string; categoryId: string }) => {
  const response = await axiosInstance.delete(`/financial-models/${financialModelId}/cogs/categories/${categoryId}`)
  return response.data
}

export const useDeleteCategoryMutation = (args: { financialModelId: string; categoryId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteCategory>[0]) => deleteCategory(data),
    mutationKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`direct-costs`, `categories`, args.categoryId, args.financialModelId]
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getDirectCostsCategoriesQueryKey(args.financialModelId)
      })
    }
  })
}

// **** PUT -> DIRECT COSTS - CATEGORY **** //

const updateCategory = async ({ financialModelId, categoryId, name }: { financialModelId: string; categoryId: string; name: string }) => {
  const response = await axiosInstance.put(`/financial-models/${financialModelId}/cogs/categories/${categoryId}`, {
    name
  })
  return response.data
}

export const useUpdateCategoryMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof updateCategory>[0]) => updateCategory(data),
    mutationKey: getDirectCostsCategoriesQueryKey(financialModelId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getDirectCostsCategoriesQueryKey(financialModelId)
      })
    }
  })
}

// **** GET -> DIRECT COSTS PERIODIC DATA **** //

type DirectCostsPeriodicData = {
  periods: DirectCostUnitCost[]
  config: DirectCostUnitCostConfig
}

const getDirectCostsPeriodicData = async (params: {
  financialModelId: string
  period_type: PeriodTypeQuery
  view?: PeriodTypeView
  categoryId?: string
  productId?: string
}) => {
  try {
    const response = await axiosInstance.get<DirectCostsPeriodicData>(`/financial-models/${params.financialModelId}/cogs/direct-costs`, {
      params: {
        period_type: params.period_type,
        view: params.view ?? `monthly`,
        ...(params.productId ? { product_id: params.productId } : {}),
        ...(params.categoryId ? { category_id: params.categoryId } : {})
      }
    })

    const [firstPeriod] = response.data.periods

    if (!firstPeriod) {
      return []
    }

    useCogsStore.getState().setScenario(firstPeriod.scenario)

    if (firstPeriod.scenario === 3 && !params.productId) {
      const allProducts = await getUnitsSettings(params.financialModelId, params.categoryId)
      const allDirectCostPromises = allProducts.unit_settings.map(product =>
        axiosInstance.get<DirectCostsPeriodicData>(`/financial-models/${params.financialModelId}/cogs/direct-costs`, {
          params: {
            period_type: params.period_type,
            view: params.view ?? `monthly`,
            product_id: product.product_id,
            ...(params.categoryId ? { category_id: params.categoryId } : {})
          }
        })
      )

      const [allResults] = await handlePromises(allDirectCostPromises)
      const successfulResults = allResults.filter(result => result.data)

      return successfulResults.map(result => result.data)
    }
    return [response.data]
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

export const directCostsPeriodicDataQueryOptions = (params: {
  financialModelId: string
  period_type: PeriodTypeQuery
  view?: PeriodTypeView
  categoryId?: string
  productId?: string
}) =>
  queryOptions({
    queryKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`direct-costs`, params.financialModelId, params.period_type, params.categoryId, params.productId, params.view ?? `monthly`]
    }),
    queryFn: () => getDirectCostsPeriodicData(params)
  })

export const useGetDirectCostsPeriodicData = (params: {
  financialModelId: string
  period_type: PeriodTypeQuery
  view?: PeriodTypeView
  categoryId?: string
  productId?: string
}) => useSuspenseQuery(directCostsPeriodicDataQueryOptions(params))

export const postDirectCostsPeriodicData = async (params: { financialModelId: string; data: DirectCostUnitCostPostBody }) => {
  const response = await axiosInstance.post(`/financial-models/${params.financialModelId}/cogs/direct-costs`, params.data)
  return response.data
}

export const postDirectCostsPeriodicDataBulk = async (params: { financialModelId: string; data: DirectCostUnitCostPostBody[] }) => {
  const response = await axiosInstance.post(`/financial-models/${params.financialModelId}/cogs/direct-costs/bulk`, {
    periods: params.data
  })
  return response.data
}

export const usePostDirectCostsPeriodicData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: DirectCostUnitCostPostBody) => postDirectCostsPeriodicData({ financialModelId, data }),
    mutationKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`direct-costs`, financialModelId]
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: COGS_BASE_KEY,
          keys: [`direct-costs`, financialModelId]
        })
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `direct-costs`]
        })
      })
    }
  })
}

// **** POST -> SWITCH COLUMN TYPE **** //
const switchCogsColumnType = async (params: ColumnSwitchRequest) => {
  const { financial_model_id, column_name, ...rest } = params
  const response = await axiosInstance.post(`/financial-models/${financial_model_id}/cogs/config/${column_name}`, undefined, { params: rest })
  return response.data
}

export const useSwitchCogsColumnType = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<ColumnSwitchRequest, `financial_model_id`>) =>
      switchCogsColumnType({
        financial_model_id: financialModelId,
        ...data
      }),
    mutationKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`switch-cogs-column-type`, financialModelId]
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: COGS_BASE_KEY,
          keys: [`direct-costs`, financialModelId]
        })
      })
    }
  })
}

const getDirectLabourSettingData = async (financialModelId: string) => {
  const response = await axiosInstance.get<{
    id: string
    financial_model_id: string
    modeling_approach: DirectLabourModelingApproachSetting
    active: boolean
    labour_department_id: string
  }>(`/financial-models/${financialModelId}/cogs/labour-setup`)
  return response.data
}

export const useGetDirectLabourSettingData = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`direct-labour-setting-data`, financialModelId]
    }),
    queryFn: () => getDirectLabourSettingData(financialModelId)
  })

const postDirectLabourSettingData = async (data: {
  financial_model_id: string
  modeling_approach: DirectLabourModelingApproachSetting
  active: boolean
}) => {
  const response = await axiosInstance.post(`/financial-models/${data.financial_model_id}/cogs/labour-setup`, data)
  return response.data
}

export const usePostDirectLabourSettingDataMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { financial_model_id: string; modeling_approach: DirectLabourModelingApproachSetting; active: boolean }) =>
      postDirectLabourSettingData(data),
    mutationKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`direct-labour-setting-data`, financialModelId]
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: COGS_BASE_KEY,
          keys: [`direct-labour-setting-data`, financialModelId]
        })
      })
    }
  })
}

const getDirectLabourData = async (financialModelId: string, period: PeriodTypeQuery, view: PeriodTypeView): Promise<GetDirectLabourDataResponse> => {
  const response = await axiosInstance.get<GetDirectLabourDataResponse>(`/financial-models/${financialModelId}/cogs/direct-labour`, {
    params: {
      period_type: period,
      view
    }
  })
  return response.data
}

export const useGetDirectLabourDataSuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`direct-labour`, financialModelId, period, view]
    }),
    queryFn: () => getDirectLabourData(financialModelId, period, view)
  })

// POST - DIRECT LABOUR ROW TYPE

const switchDirectLabourRowType = async (
  financialModelId: string,
  data: Omit<DirectLabourColumnSwitchRequest, 'financial_model_id'>
): Promise<string> => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/cogs/direct-labour/config/${data.column_name}`, undefined, {
    params: {
      period_type: data.period_type,
      col_type: data.col_type,
      year: data.year,
      month: data.month
    }
  })
  return response.data
}

export const useSwitchDirectLabourRowTypeMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<DirectLabourColumnSwitchRequest, 'financial_model_id'>) => switchDirectLabourRowType(financialModelId, data),
    mutationKey: createQueryKey({
      baseKey: COGS_BASE_KEY,
      keys: [`switch-direct-labour-row-type`, financialModelId]
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: COGS_BASE_KEY,
          keys: [`direct-labour`, financialModelId]
        })
      })
    }
  })
}

export const postDirectLabourData = async (financialModelId: string, data: PostDirectLabourBody): Promise<string> => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/cogs/direct-labour`, data)

  return response.data
}

export const postDirectLabourDataBulk = async (financialModelId: string, data: PostDirectLabourBody[]) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/cogs/direct-labour/bulk`, {
    periods: data
  })

  return response.data
}

export const getPostDirectLabourDataMutationArgs = (financialModelId: string, queryClient: QueryClient) => ({
  mutationFn: (data: PostDirectLabourBody) => postDirectLabourData(financialModelId, data),
  onSuccess: (_: unknown, variables: PostDirectLabourBody) => {
    const { period_type, view } = variables
    queryClient.invalidateQueries({
      queryKey: createQueryKey({
        baseKey: COGS_BASE_KEY,
        keys: [`direct-labour`, financialModelId, period_type, view]
      })
    })
  }
})

export const getBulkDirectLabourDataMutationArgs = (financialModelId: string) => ({
  mutationFn: (data: PostDirectLabourBody) => postDirectLabourData(financialModelId, data)
})

export const usePostDirectLabourData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation(getPostDirectLabourDataMutationArgs(financialModelId, queryClient))
}

export const getCogsTotalResults = async <P extends PeriodTypeView = PeriodTypeView>(
  params: CogsTotalResultRequest<P>
): Promise<CogsTotalTable<P>> => {
  const response = await axiosInstance.get<CogsTotalTable<P>>(`/financial-models/${params.financial_model_id}/cogs/total-results-v2`, {
    params: { view: params.view }
  })
  return response.data
}

export const useGetCogsTotalResults = <P extends PeriodTypeView = PeriodTypeView>(params: CogsTotalResultRequest<P>) =>
  useSuspenseQuery({
    queryFn: () => getCogsTotalResults<P>(params),
    queryKey: createQueryKey({
      baseKey: TOTALS_KEY,
      keys: [`direct-costs`, `direct-labour`, params.financial_model_id, params.view]
    })
  })
